import { ADD_COPY, type AddState } from "../app/add-payee-model.js";
import { searchPayees, type PayeeDetail, type PayeeRow, type PayeesModel } from "../app/payees-model.js";
import { el } from "../lib/dom.js";
import { icon } from "../lib/icon.js";
import type { AimHandlers } from "./aim.js";
import { loadFailed, setBusy } from "./busy.js";
import { CheckSignView } from "./check-sign.js";
import { tabBar, type Tab } from "./tabs.js";

/**
 * 17 Payees (DCTA-111 run 2; UX spec §E.4/§E.6/§E.7, mock-up 17-payees.html), pushed under Me: the
 * list and a payee's page are two screens in one stack, as Home and an account are (§C.5) — the page
 * pushes in from the right, the list slides under a veil, the tab bar stays.
 *
 * Every string from the bank goes in as text (`el()` children are text nodes): a nickname with markup
 * or an instruction in it is drawn as those characters (§E.7, the seed's `pay_inj001`).
 *
 * DCTA-120: a third screen in the stack, 17 at `add` (the list's +, and the assistant's pointer): the
 * PayNow name check. DCTA-124 (§F.2 row 13): then the customer's own name for them and Check and sign —
 * a fourth screen (`CheckSignView`) carrying the bank's card for the signed `payee_add` step; once the
 * bank has added them, the payee's page, saying so (13f).
 *
 * Not drawn — no route in the lab (backend asks): Pay from here (the form route, ASK-4), Rename and
 * Remove (ASK-12, ASK-14), payments to this payee (ASK-12).
 */
export interface PayeesHandlers {
  onBack(): void;
  onOpen(id: string): void;
  onDetailBack(): void;
  onRetry(): void;
  /** The list's +: 17 at `add`. */
  onAdd(): void;
  /** The add screen's own back (to the list, or to the chat it came from). */
  onAddBack(): void;
  /** Continue: the words typed, to be read as a PayNow proxy by the controller. */
  onLookup(typed: string): void;
  /** Talk to us (the bank's number, on a sheet). */
  onTalk(): void;
  /** DCTA-124 (13a): Check and sign — the customer's own name for them, as typed (the controller checks it). */
  onCheck(nickname: string): void;
  /** Check and sign's own back. */
  onSignBack(): void;
  onTab(tab: Tab): void;
  readonly assistant: AimHandlers;
}

export class PayeesView {
  readonly parts: readonly HTMLElement[];
  readonly listBody: HTMLElement;
  readonly detailBody: HTMLElement;
  readonly back: HTMLButtonElement;
  readonly detailBack: HTMLButtonElement;
  readonly #h: PayeesHandlers;
  readonly #note: HTMLElement;
  readonly #searchBox: HTMLElement;
  readonly #search: HTMLInputElement;
  readonly #plist: HTMLElement;
  readonly #list: HTMLElement;
  readonly #detail: HTMLElement;
  readonly #content: HTMLElement;
  readonly #add: HTMLElement;
  readonly addBody: HTMLElement;
  readonly addBack: HTMLButtonElement;
  readonly #number: HTMLInputElement;
  readonly #numberBox: HTMLElement;
  readonly #addResult: HTMLElement;
  readonly #addActs: HTMLElement;
  readonly #addHelp: HTMLElement;
  readonly #nickFld: HTMLElement;
  readonly #nick: HTMLInputElement;
  readonly #nickBox: HTMLElement;
  readonly #nickHelp: HTMLElement;
  #nickErr: HTMLElement | undefined;
  /** DCTA-124: Check and sign, pushed over 17 at `add`. */
  readonly signView: CheckSignView;
  /** The payee's page is showing the add's result (13f): its back leaves the add screen. */
  #showsAdded = false;
  readonly #done: HTMLElement;
  #addState: AddState = { kind: "ask" };
  #addErr: HTMLElement | undefined;
  #addButton: HTMLButtonElement | undefined;
  #model: PayeesModel | undefined;
  /** The row that opened the page: focus goes back to it. */
  #opener: string | undefined;

  constructor(handlers: PayeesHandlers) {
    this.#h = handlers;
    this.back = el("button", { class: "raw icobtn back", type: "button", aria: { label: "Back to Me" } }, [icon("i-chev-l")]);
    this.back.addEventListener("click", () => this.#h.onBack());
    this.#note = el("span", { class: "vis-hidden loading-note", role: "status" });
    this.#search = el("input", {
      class: "q",
      type: "search",
      placeholder: "Name or number",
      autocomplete: "off",
      aria: { label: "Search payees by name or number" },
    }) as HTMLInputElement;
    this.#search.setAttribute("enterkeyhint", "search");
    this.#search.addEventListener("input", () => this.#paint());
    this.#searchBox = el("label", { class: "inp search" }, [icon("i-search"), this.#search]);
    this.#plist = el("div", { class: "plist" });
    this.listBody = el("div", { class: "body" }, [this.#note, this.#searchBox, this.#plist, el("div", { class: "home-end", aria: { hidden: "true" } })]);
    const add = el("button", { class: "raw icobtn add", type: "button", aria: { label: "Add a payee" } }, [icon("i-plus")]);
    add.addEventListener("click", () => this.#h.onAdd());
    this.#addButton = add;
    this.#list = el("div", { class: "scr s-list" }, [
      el("div", { class: "topbar" }, [this.back, el("span", { class: "t" }, ["Payees"]), add]),
      this.listBody,
      el("span", { class: "veil", aria: { hidden: "true" } }),
    ]);

    this.detailBack = el("button", { class: "raw icobtn back", type: "button", aria: { label: "Back to Payees" } }, [icon("i-chev-l")]);
    // The page of a payee just added is the add screen's result: its back leaves the add screen (13f).
    this.detailBack.addEventListener("click", () => (this.#showsAdded ? this.#h.onAddBack() : this.#h.onDetailBack()));
    this.#done = el("div", { class: "banner done", role: "status", hidden: true });
    this.#content = el("div", { class: "dcontent" });
    this.detailBody = el("div", { class: "body" }, [this.#content, el("div", { class: "home-end", aria: { hidden: "true" } })]);
    this.#detail = el("div", { class: "scr s-detail off-r", role: "region", aria: { label: "Payee", hidden: "true" } }, [
      el("div", { class: "topbar" }, [this.detailBack, el("span", { class: "t", aria: { hidden: "true" } }), el("span", { class: "topbar-pad", aria: { hidden: "true" } })]),
      this.detailBody,
    ]);
    this.#detail.inert = true;

    // 17 at `add` (DCTA-120): the field is a real <input> in the design's `.inp` (as the search is), 16 px
    // or more, labelled, its help and — when there is one — its error tied to it.
    this.addBack = el("button", { class: "raw icobtn back", type: "button", aria: { label: "Back to Payees" } }, [icon("i-chev-l")]);
    this.addBack.addEventListener("click", () => this.#h.onAddBack());
    this.#number = el("input", {
      id: "pn-number",
      class: "q",
      type: "text",
      autocomplete: "off",
      aria: { describedby: "pn-help" },
    }) as HTMLInputElement;
    this.#number.setAttribute("spellcheck", "false");
    this.#number.setAttribute("enterkeyhint", "go");
    this.#number.setAttribute("autocapitalize", "characters");
    this.#number.addEventListener("input", () => {
      if (this.#addState.kind !== "ask" && this.#addState.kind !== "checking") this.addState({ kind: "ask" });
    });
    this.#number.addEventListener("keydown", (e) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      this.#continue();
    });
    this.#numberBox = el("label", { class: "inp field" }, [this.#number]);
    // The directory's answer is announced when it arrives (Continue gives no other sign to a screen reader).
    this.#addResult = el("div", { class: "aresult", role: "status" });
    this.#addHelp = el("span", { class: "help", id: "pn-help" }, [ADD_COPY.help]);
    this.#addActs = el("div", { class: "acts" });
    // DCTA-124 (13c, §E.6): the customer's own name for them — optional, outside the announced answer
    // (a live region never holds an input), shown once the bank has named the number.
    this.#nick = el("input", { id: "pn-nick", class: "q", type: "text", autocomplete: "off", aria: { describedby: "pn-nick-help" } }) as HTMLInputElement;
    this.#nick.setAttribute("enterkeyhint", "go");
    this.#nick.setAttribute("autocapitalize", "words");
    this.#nick.addEventListener("input", () => {
      if (this.#nickErr !== undefined) this.nickError(undefined);
    });
    this.#nick.addEventListener("keydown", (e) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      this.#check();
    });
    this.#nickBox = el("label", { class: "inp field" }, [this.#nick]);
    this.#nickHelp = el("span", { class: "help", id: "pn-nick-help" }, [ADD_COPY.nickname.help]);
    this.#nickFld = el("div", { class: "fld nick", hidden: true }, [el("label", { class: "l", for: "pn-nick" }, [ADD_COPY.nickname.label]), this.#nickBox, this.#nickHelp]);
    this.addBody = el("div", { class: "body" }, [
      el("div", { class: "fld" }, [
        el("label", { class: "l", for: "pn-number" }, [ADD_COPY.label]),
        this.#numberBox,
        this.#addHelp,
      ]),
      this.#addResult,
      this.#nickFld,
      this.#addActs,
      el("div", { class: "home-end", aria: { hidden: "true" } }),
    ]);
    this.#add = el("div", { class: "scr s-add off-r", role: "region", aria: { labelledby: "add-title", hidden: "true" } }, [
      el("div", { class: "topbar" }, [this.addBack, el("span", { class: "t", id: "add-title" }, [ADD_COPY.title]), el("span", { class: "topbar-pad", aria: { hidden: "true" } })]),
      this.addBody,
    ]);
    this.#add.inert = true;
    this.addState({ kind: "ask" });
    this.signView = new CheckSignView({ onBack: () => this.#h.onSignBack() }, this.#add);

    const bar = tabBar("me", { onTab: (t) => this.#h.onTab(t), ...this.#h.assistant }, { pushed: true });
    this.parts = [el("div", { class: "screens" }, [this.#list, this.#detail, this.#add, this.signView.element]), bar.dock];
  }

  get addOpen(): boolean {
    return !this.#add.classList.contains("off-r");
  }

  /**
   * 17 at `add`, pushed in from the right over the list (the list's +), or shown at once over the chat
   * (the assistant's pointer: `instant`, the list under it never seen). Focus to the field.
   */
  pushAdd(backLabel: string, instant = false): void {
    this.addBack.setAttribute("aria-label", backLabel);
    this.#number.value = "";
    this.addState({ kind: "ask" });
    if (instant) this.#add.dataset["instant"] = "true";
    this.#add.dataset["from"] = instant ? "chat" : "list";
    this.#add.classList.remove("off-r");
    this.#list.classList.add("under");
    this.#list.inert = true;
    this.#list.setAttribute("aria-hidden", "true");
    this.#add.inert = false;
    this.#add.removeAttribute("aria-hidden");
    this.addBody.scrollTop = 0;
    this.#number.focus({ preventScroll: true });
    if (instant) requestAnimationFrame(() => delete this.#add.dataset["instant"]);
  }

  popAdd(): void {
    this.signView.pop();
    if (this.#showsAdded) {
      // The page of the payee just added was the add's result: it goes with the add screen.
      this.#showsAdded = false;
      this.#done.remove();
      this.popDetail();
      this.detailBack.setAttribute("aria-label", "Back to Payees");
      return;
    }
    if (!this.addOpen) return;
    const fromList = this.#add.dataset["from"] === "list";
    this.#add.classList.add("off-r");
    this.#add.inert = true;
    this.#add.setAttribute("aria-hidden", "true");
    this.#list.classList.remove("under");
    this.#list.inert = false;
    this.#list.removeAttribute("aria-hidden");
    this.#number.value = "";
    this.addState({ kind: "ask" });
    // Back over the list: focus to the + that opened it (over the chat, the chat is mounted again instead).
    if (fromList) this.#addButton?.focus({ preventScroll: true });
  }

  /** The add screen's state: the result under the field and its one action. */
  addState(state: AddState): void {
    this.#addState = state;
    const go = (label: string, cls: string, onSelect: () => void): HTMLButtonElement => {
      const b = el("button", { class: cls, type: "button" }, [label]);
      b.addEventListener("click", () => {
        if (b.getAttribute("aria-busy") === "true") return;
        onSelect();
      });
      return b;
    };
    this.#addResult.replaceChildren();
    this.#addErr?.remove();
    this.#addErr = undefined;
    this.#number.removeAttribute("aria-invalid");
    this.#number.setAttribute("aria-describedby", "pn-help");
    this.#addHelp.hidden = false;
    this.#number.readOnly = state.kind === "checking";
    this.#numberBox.classList.toggle("invalid", state.kind === "error");
    this.#nickFld.hidden = state.kind !== "found";
    if (state.kind !== "found") {
      this.#nick.value = "";
      this.nickError(undefined);
    }
    switch (state.kind) {
      case "ask":
      case "checking": {
        const b = go(ADD_COPY.continue, "raw btn", () => this.#continue());
        b.setAttribute("aria-busy", state.kind === "checking" ? "true" : "false");
        this.#addActs.replaceChildren(b);
        return;
      }
      case "found":
        this.#addResult.replaceChildren(
          el("div", { class: "fld" }, [
            el("span", { class: "l" }, [ADD_COPY.found]),
            el("div", { class: "list feed found" }, [
              el("div", { class: "item" }, [
                el("span", { class: "ic", aria: { hidden: "true" } }, [state.initial]),
                el("span", { class: "tx" }, [el("b", {}, [state.name]), el("small", {}, [state.sub])]),
              ]),
            ]),
          ]),
        );
        // §F.2 13a: Check and sign (the interim "call us" end retired with ASK-13, 13l).
        this.#addActs.replaceChildren(go(ADD_COPY.check, "raw btn", () => this.#check()));
        return;
      case "error": {
        const err = el("span", { class: "err", id: "pn-err", role: "alert" }, [icon("i-alert"), el("span", {}, [state.text])]);
        this.#numberBox.after(err);
        this.#addErr = err;
        this.#number.setAttribute("aria-invalid", "true");
        // The sentence about the field takes the help line's place (17's add-blocked).
        this.#addHelp.hidden = true;
        this.#number.setAttribute("aria-describedby", "pn-err");
        this.#addActs.replaceChildren(state.talk ? go(ADD_COPY.talk, "raw btn ghost", () => this.#h.onTalk()) : go(ADD_COPY.continue, "raw btn", () => this.#continue()));
        return;
      }
    }
  }

  #continue(): void {
    if (this.#addState.kind === "checking") return;
    this.#h.onLookup(this.#number.value);
  }

  #check(): void {
    if (this.#addState.kind !== "found") return;
    this.#h.onCheck(this.#nick.value);
  }

  /** A sentence about the customer's name for them (the bank's display rule), tied to the field — or none. */
  nickError(text: string | undefined): void {
    this.#nickErr?.remove();
    this.#nickErr = undefined;
    this.#nickBox.classList.toggle("invalid", text !== undefined);
    if (text === undefined) {
      this.#nick.removeAttribute("aria-invalid");
      this.#nick.setAttribute("aria-describedby", "pn-nick-help");
      this.#nickHelp.hidden = false;
      return;
    }
    const err = el("span", { class: "err", id: "pn-nick-err", role: "alert" }, [icon("i-alert"), el("span", {}, [text])]);
    this.#nickBox.after(err);
    this.#nickErr = err;
    this.#nick.setAttribute("aria-invalid", "true");
    this.#nick.setAttribute("aria-describedby", "pn-nick-err");
    this.#nickHelp.hidden = true;
    this.#nick.focus({ preventScroll: true });
  }

  /** Back from Check and sign: the name and the customer's name for them as they were, focus on Check and sign. */
  focusCheck(): void {
    this.#addActs.querySelector<HTMLButtonElement>("button")?.focus({ preventScroll: true });
  }

  /**
   * §F.2 13f (mock-up `added-cooling`): the bank added them — their page, as the bank reads it now, with
   * "{Name} added" said once (a status) above it; Check and sign and the add screen leave. Its back
   * leaves the add screen (`backLabel`: where that goes).
   */
  showAdded(d: PayeeDetail, done: string, backLabel: string): void {
    this.signView.pop();
    this.#add.classList.add("off-r");
    this.#add.inert = true;
    this.#add.setAttribute("aria-hidden", "true");
    this.#showsAdded = true;
    this.pushDetail(d);
    this.detailBack.setAttribute("aria-label", backLabel);
    this.#done.replaceChildren(icon("i-check"), el("span", { class: "bt" }, [el("b", {}, [done])]));
    this.#done.hidden = false;
    this.#content.prepend(this.#done);
  }

  get detailOpen(): boolean {
    return !this.#detail.classList.contains("off-r");
  }

  loading(): void {
    this.#busy(true, "Loading payees");
    this.#plist.replaceChildren(el("span", { class: "sk sk-row" }), el("span", { class: "sk sk-row" }), el("span", { class: "sk sk-row" }));
  }

  show(m: PayeesModel): void {
    this.#model = m;
    this.#busy(false, "");
    this.#paint();
  }

  unreachable(): void {
    this.#model = undefined;
    this.#busy(false, "");
    this.#searchBox.hidden = true;
    this.#plist.replaceChildren(loadFailed("Couldn't load your payees", "Your payees and money are not affected.", () => this.#h.onRetry()));
  }

  /** A payee's page, pushed in from the right (focus to its back button). */
  pushDetail(d: PayeeDetail, opener?: string): void {
    this.#opener = opener;
    const blocks: HTMLElement[] = [
      el("div", { class: "who2 lg" }, [
        el("span", { class: "ic", aria: { hidden: "true" } }, [d.initial]),
        el("span", { class: "tx" }, [el("b", { id: "payee-name" }, [d.name]), el("small", {}, [d.registered])]),
      ]),
    ];
    if (d.wait !== undefined) {
      blocks.push(el("div", { class: "banner" }, [icon("i-clock"), el("span", { class: "bt" }, [el("b", {}, [d.wait.title]), el("span", {}, [d.wait.sub])])]));
    }
    blocks.push(
      el("div", { class: "sec" }, [el("h3", {}, ["What the bank holds"])]),
      el("div", { class: "list kv" }, d.kv.map(([k, v]) => el("div", {}, [el("span", { class: "k" }, [k]), el("span", { class: "v" }, [v])]))),
    );
    this.#content.replaceChildren(...blocks);
    this.#detail.setAttribute("aria-labelledby", "payee-name");
    this.#detail.removeAttribute("aria-label");
    this.detailBody.scrollTop = 0;
    this.#detail.classList.remove("off-r");
    this.#list.classList.add("under");
    this.#swap(true);
    this.detailBack.focus({ preventScroll: true });
  }

  popDetail(): void {
    if (!this.detailOpen) return;
    this.#detail.classList.add("off-r");
    this.#list.classList.remove("under");
    this.#swap(false);
    const opener = this.#opener === undefined ? undefined : this.#plist.querySelector<HTMLElement>(`.item[data-p="${CSS.escape(this.#opener)}"]`);
    opener?.focus({ preventScroll: true });
    this.#opener = undefined;
  }

  #paint(): void {
    const m = this.#model;
    if (m === undefined) return;
    const all = [...m.fresh, ...m.all];
    if (all.length === 0) {
      this.#searchBox.hidden = true;
      this.#plist.replaceChildren(
        el("div", { class: "empty" }, [
          el("span", { class: "ic", aria: { hidden: "true" } }, [icon("i-users")]),
          el("b", {}, ["No payees yet"]),
          el("p", {}, ["The people you pay will be listed here."]),
        ]),
      );
      return;
    }
    this.#searchBox.hidden = false;
    const q = this.#search.value;
    if (q.trim() !== "") {
      const hits = searchPayees(all, q);
      this.#plist.replaceChildren(hits.length === 0 ? el("p", { class: "nomatch" }, [`No payee matches “${q.trim()}”.`]) : this.#rows(hits));
      return;
    }
    const blocks: HTMLElement[] = [];
    if (m.fresh.length > 0) blocks.push(el("div", { class: "sec" }, [el("h3", {}, ["New"])]), this.#rows(m.fresh));
    if (m.all.length > 0) blocks.push(el("div", { class: "sec" }, [el("h3", {}, ["All"])]), this.#rows(m.all));
    this.#plist.replaceChildren(...blocks);
  }

  #rows(rows: readonly PayeeRow[]): HTMLElement {
    return el(
      "div",
      { class: "list feed" },
      rows.map((r) => {
        const b = el("button", { class: "raw item", type: "button", data: { p: r.id } }, [
          el("span", { class: "ic", aria: { hidden: "true" } }, [r.initial]),
          el("span", { class: "tx" }, [el("b", {}, [r.name, ...(r.flag === undefined ? [] : [" ", el("span", { class: "flag" }, [r.flag])])]), el("small", {}, [r.sub])]),
          icon("i-chev-r", "chev"),
        ]);
        b.addEventListener("click", () => this.#h.onOpen(r.id));
        return b;
      }),
    );
  }

  #busy(on: boolean, note: string): void {
    setBusy(this.listBody, this.#note, this.#plist, on, note);
  }

  #swap(detail: boolean): void {
    this.#list.inert = detail;
    this.#detail.inert = !detail;
    if (detail) {
      this.#list.setAttribute("aria-hidden", "true");
      this.#detail.removeAttribute("aria-hidden");
    } else {
      this.#list.removeAttribute("aria-hidden");
      this.#detail.setAttribute("aria-hidden", "true");
    }
  }
}
